import { useEffect, useRef, useState } from 'react';

/**
 * Reveals children once they scroll into view.
 *
 * Props:
 *   variant   — 'up' | 'left' | 'right' | 'scale' | 'blur' (default 'up')
 *   delay     — reveal delay in ms (for staggering siblings)
 *   threshold — IntersectionObserver threshold (default 0.15)
 *   once      — disconnect after first reveal (default true)
 */
export default function ScrollReveal({
  children,
  variant = 'up',
  delay = 0,
  threshold = 0.15,
  once = true,
  className = '',
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Respect users who prefer no motion — reveal immediately.
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setVisible(false);
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, once]);

  return (
    <div
      ref={ref}
      className={`scroll-reveal scroll-reveal--${variant}${visible ? ' scroll-reveal--visible' : ''} ${className}`.trim()}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  );
}
